import { useCallback, useEffect, useMemo, useState } from "react";
import { Activity, Database, Plus, RefreshCw, Table2, Trash2, X } from "lucide-react";
import { api, errorText, timeAgo } from "@/lib/api";

function ProjectForm({ onCreated, onError }) {
  const [name, setName] = useState("");
  const submit = async e => {
    e.preventDefault();
    try { onCreated(await api.addProject(name)); setName(""); } catch (err) { onError(errorText(err, "Could not add project")); }
  };
  return (
    <form onSubmit={submit} className="inline-form">
      <input data-testid="project-name-input" required minLength={2} value={name} onChange={e => setName(e.target.value)} placeholder="New project name" />
      <button className="save-btn compact" data-testid="save-project-button"><Plus size={14} /> Add project</button>
    </form>
  );
}

function SheetForm({ projects, onSaved, onError }) {
  const [form, setForm] = useState({ url: "", project_id: "", auto_sync: true });
  const [saving, setSaving] = useState(false);
  const selectable = useMemo(() => projects.filter(p => p.id !== "all"), [projects]);
  const projectId = form.project_id || selectable[0]?.id || "";
  const submit = async e => {
    e.preventDefault();
    setSaving(true);
    try { onSaved(await api.addSheet({ ...form, project_id: projectId })); setForm(f => ({ ...f, url: "" })); } catch (err) { onError(errorText(err, "Could not save this connection")); }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="sheet-form">
      <label>Google Sheet workbook URL<input data-testid="sheet-url-input" required value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} placeholder="https://docs.google.com/spreadsheets/d/…" /></label>
      <label>Project<select data-testid="sheet-project-selector" required disabled={!selectable.length} value={projectId} onChange={e => setForm(f => ({ ...f, project_id: e.target.value }))}>{selectable.length ? selectable.map(p => <option key={p.id} value={p.id}>{p.name}</option>) : <option value="">Create a project first</option>}</select></label>
      <label className="check-row"><input type="checkbox" data-testid="sheet-auto-sync-toggle" checked={form.auto_sync} onChange={e => setForm(f => ({ ...f, auto_sync: e.target.checked }))} /> Auto-sync every 60 seconds</label>
      <button className="save-btn" data-testid="save-sheet-button" disabled={saving || !projectId}><Plus size={16} /> {saving ? "Connecting & syncing…" : "Connect workbook"}</button>
    </form>
  );
}

function SourceRow({ item, onSync, onRemove }) {
  const counts = Object.entries(item.row_counts || {}).map(([tab, rows]) => `${tab} ${rows}`).join(" · ");
  return (
    <div className="source-row" data-testid={`source-row-${item.id}`}>
      <div className="source-icon"><Table2 size={16} /></div>
      <div className="source-body">
        <strong>{item.title || "Workbook"} <em>→ {item.project_name}</em></strong>
        <span>{counts || "No rows synced yet"} · synced {timeAgo(item.last_sync)}{item.auto_sync ? " · auto" : ""}</span>
        {item.last_error && <span className="source-error" data-testid={`source-error-${item.id}`}>{item.last_error}</span>}
      </div>
      <span className={`source-status ${item.status === "Live" ? "status-live" : ""}`} data-testid={`source-status-${item.id}`}>{item.status}</span>
      <button className="icon-btn" data-testid={`sync-sheet-${item.id}`} onClick={() => onSync(item.id)} title="Sync now"><RefreshCw size={15} /></button>
      <button className="icon-btn danger" data-testid={`remove-sheet-${item.id}`} onClick={() => onRemove(item.id)} title="Disconnect"><Trash2 size={15} /></button>
    </div>
  );
}

function StatusPanel({ status, onClearCache }) {
  if (!status) return null;
  return (
    <div className="status-grid" data-testid="admin-status">
      <div><span>AI provider</span><strong>{status.ai.configured ? status.ai.primary_model : "Not configured"}</strong><small>fallback {status.ai.fallback_model || "off"} · {status.ai.calls.primary}/{status.ai.calls.fallback}/{status.ai.calls.failed} primary/fallback/failed</small></div>
      <div><span>Google Sheets</span><strong>{status.google.configured ? "Service account active" : "Missing credentials"}</strong><small>{status.google.service_account || "—"} · every {status.google.sync_interval_seconds}s</small></div>
      <div><span>Synced rows</span><strong>{status.rows.bookings} bookings · {status.rows.transactions} transactions</strong><small>normalized in database</small></div>
      <div><span>Report cache</span><strong>{status.cache.entries} entries · {status.cache.hits} hits</strong><small>{status.cache.backend}, TTL {status.cache.ttl_seconds}s · <button className="link-btn" data-testid="clear-cache-button" onClick={onClearCache}>clear</button></small></div>
    </div>
  );
}

function SyncLogs({ logs }) {
  if (!logs.length) return <div className="source-empty">No sync activity yet.</div>;
  return (
    <div className="log-list" data-testid="sync-logs">
      {logs.map(log => (
        <div className={`log-row ${log.status}`} key={log.id}>
          <i></i>
          <span>{new Date(log.started_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })} · {log.trigger}</span>
          <strong>{log.status === "success" ? Object.entries(log.tabs).filter(([, t]) => t.status === "ok").map(([k, t]) => `${k} ${t.rows}`).join(", ") : log.error}</strong>
        </div>
      ))}
    </div>
  );
}

export function AdminPanel({ projects, onClose, onProjectsChanged }) {
  const [saved, setSaved] = useState([]);
  const [logs, setLogs] = useState([]);
  const [status, setStatus] = useState(null);
  const [notice, setNotice] = useState("");

  const refresh = useCallback(() => {
    api.sheets().then(setSaved).catch(() => {});
    api.syncLogs().then(setLogs).catch(() => {});
    api.adminStatus().then(setStatus).catch(() => {});
  }, []);
  useEffect(() => { refresh(); const t = setInterval(refresh, 15000); return () => clearInterval(t); }, [refresh]);

  const patch = useCallback((id, changes) => setSaved(items => items.map(item => item.id === id ? { ...item, ...changes } : item)), []);
  const sync = useCallback(async id => {
    patch(id, { status: "Syncing…" });
    try { const r = await api.syncSheet(id); patch(id, r.sheet); setNotice(r.message); onProjectsChanged(); }
    catch (err) { patch(id, { status: "Sync failed" }); setNotice(errorText(err, "Could not start sync")); }
    refresh();
  }, [patch, refresh, onProjectsChanged]);
  const remove = useCallback(async id => {
    try { await api.removeSheet(id); setSaved(items => items.filter(i => i.id !== id)); setNotice("Connection removed"); onProjectsChanged(); refresh(); }
    catch (err) { setNotice(errorText(err, "Could not remove connection")); }
  }, [refresh, onProjectsChanged]);

  return (
    <div className="modal-backdrop">
      <aside className="admin-panel" data-testid="admin-panel">
        <div className="panel-header">
          <div><div className="eyebrow"><span className="eyebrow-line"></span> Data operations</div><h2>Source connections</h2></div>
          <button className="icon-btn" data-testid="close-admin-panel-button" onClick={onClose}><X size={19} /></button>
        </div>
        <p className="panel-copy">Each project workbook needs a <b>Bookings</b> tab and optionally a <b>Transactions</b> tab. Share it with the service account as Viewer, paste the URL, and the first sync runs immediately.</p>
        <StatusPanel status={status} onClearCache={() => api.clearCache().then(() => { setNotice("Cache cleared"); refresh(); })} />
        <div className="connected-heading"><span>Projects</span><span>{projects.filter(p => p.id !== "all").length}</span></div>
        <ProjectForm onCreated={p => { setNotice(`Project ${p.name} added`); onProjectsChanged(); }} onError={setNotice} />
        <div className="connected-heading"><span>Connect a workbook</span></div>
        <SheetForm projects={projects} onSaved={item => { setSaved(prev => [item, ...prev]); setNotice(item.status === "Live" ? `Connected · ${item.title} synced` : item.last_error || item.status); onProjectsChanged(); refresh(); }} onError={setNotice} />
        {notice && <div className="success-note" data-testid="sheet-save-status">{notice}</div>}
        <div className="connected-heading"><span>Connected sources</span><span>{saved.length} sources</span></div>
        <div className="source-list">
          {saved.length ? saved.map(item => <SourceRow key={item.id} item={item} onSync={sync} onRemove={remove} />) : <div className="source-empty">No live connections yet. Connect your first workbook above.</div>}
        </div>
        <div className="connected-heading"><span><Activity size={11} /> Sync log</span><span>last {logs.length}</span></div>
        <SyncLogs logs={logs} />
        <div className="pipeline-note"><Database size={16} /><div><strong>Schema-tolerant importer</strong><span>Required columns are validated per tab; renamed/extra columns are kept as metadata and new columns are detected on every sync.</span></div></div>
      </aside>
    </div>
  );
}
